import React from "react";
import { motion } from "framer-motion"

/**
 * BlobImage – image clipped INSIDE your Blobmaker SVG path.
 * Robust responsive version (no React 18 features, no CSS aspect-ratio needed).
 *
 * It uses a square wrapper with the classic padding-top trick so it won't collapse
 * to 0px height even inside flex/grid containers.
 *
 * Usage examples:
 *   <BlobImage src="/images/hero.jpg" />
 *   <div style={{maxWidth: 420}}><BlobImage src="/images/hero.jpg" /></div>
 *   <BlobImage src="/images/hero.jpg" maxWidth={360} stroke="#fff8" strokeWidth={3} />
 */
export default function BlobImage({
  src,
  fill = "#7D2E89",
  stroke,
  strokeWidth = 0,
  width = 175,
  height = 200,
  imageHeight = height,
  imageWidth = width,
  className,
  maxWidth = 500,
  ariaLabel = "Emily Gamarro",
  d
}: {
  src: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  width?: number;
  height?: number;
  imageHeight?: number;
  imageWidth?: number;
  className?: string;
  maxWidth?: number;
  ariaLabel?: string;
  d?: string
}) {
  // Unique ids for clipPath so multiple blobs on a page do not collide.
  const clipId = React.useMemo(
    () => `clip-${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const customViewBox = `0 0 ${width} ${height}`

  return (
    <motion.div
      className={[
        "blob-image-wrapper",
        className || "",
      ].join(" ")}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: maxWidth,
      }}
    >
      {/* Intrinsic square ratio: keeps height even if parent doesn't set one */}
      <div style={{ paddingTop: "100%" }} />

      {/* Absolutely-positioned SVG that fills the square */}
      <svg
        viewBox={customViewBox}
        aria-label={ariaLabel}
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{ariaLabel}</title>
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            <path d={d} transform="translate(100 100)" />
          </clipPath>
        </defs>

        <path d={d} transform="translate(100 100)" fill={fill} />

        <image
          x={0}
          y={0}
          width={imageWidth}
          height={imageHeight}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#${clipId})`}
          xlinkHref={src} 
        />

        {/* Optional outline */}
        {stroke && strokeWidth > 0 && (
          <path
            d={d}
            transform="translate(100 100)"
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
      </svg>
    </motion.div>
  );
}
